import { z } from 'zod';

/**
 * Where one step of a case stands (`03-verification.md`).
 *
 * - `pending` — materialized, nothing sent;
 * - `submitted` — the member has sent their evidence or booked their slot;
 * - `passed` / `failed` — a **reviewer** judged it;
 * - `waived` — a reviewer skipped it for this case (audited). Distinct from a
 *   step that never materialized because the gender-scoped setting switched it
 *   off: that step has no row at all, and the difference is the difference
 *   between "we decided not to ask" and "policy does not ask".
 */
export const VERIFICATION_STEP_STATUSES = [
  'pending',
  'submitted',
  'passed',
  'failed',
  'waived',
] as const;

export const VerificationStepStatusSchema = z.enum(VERIFICATION_STEP_STATUSES);

export type VerificationStepStatus = (typeof VERIFICATION_STEP_STATUSES)[number];

/** What materialization writes for every step of a fresh case. */
export const INITIAL_STEP_STATUS: VerificationStepStatus = 'pending';

const SATISFIED: ReadonlySet<VerificationStepStatus> = new Set<VerificationStepStatus>([
  'passed',
  'waived',
]);

const RESOLVED: ReadonlySet<VerificationStepStatus> = new Set<VerificationStepStatus>([
  'passed',
  'failed',
  'waived',
]);

/**
 * The step's requirement is met.
 *
 * **`submitted` is deliberately not satisfied.** "The member sent it" and "a
 * reviewer accepted it" are different facts, and a completeness check that
 * conflated them would report a case ready before anybody had looked at it.
 */
export function isSatisfiedStepStatus(status: VerificationStepStatus): boolean {
  return SATISFIED.has(status);
}

/** A reviewer has acted on the step — it is no longer waiting on anyone. */
export function isResolvedStepStatus(status: VerificationStepStatus): boolean {
  return RESOLVED.has(status);
}
